import { useState } from "react";
import { Bot } from "lucide-react";

// Shared pre-chat contact form (Name / Email / Country code + Phone + consent).
// Used by the public full-screen widget and the in-settings admin preview so
// both surfaces collect the same lead the same way. Purely responsible for
// validation + the lead POST; the caller decides what to do onComplete (seed a
// conversation, flip a "done" flag, persist a sessionStorage marker, etc.).
export const PRECHAT_COUNTRY_CODES: { label: string; code: string }[] = [
  { label: "US/Canada (+1)", code: "+1" },
  { label: "United Kingdom (+44)", code: "+44" },
  { label: "India (+91)", code: "+91" },
  { label: "Australia (+61)", code: "+61" },
  { label: "Germany (+49)", code: "+49" },
  { label: "France (+33)", code: "+33" },
  { label: "Spain (+34)", code: "+34" },
  { label: "Italy (+39)", code: "+39" },
  { label: "Brazil (+55)", code: "+55" },
  { label: "Mexico (+52)", code: "+52" },
  { label: "Nigeria (+234)", code: "+234" },
  { label: "South Africa (+27)", code: "+27" },
  { label: "UAE (+971)", code: "+971" },
  { label: "Singapore (+65)", code: "+65" },
  { label: "Japan (+81)", code: "+81" },
  { label: "China (+86)", code: "+86" },
];

export interface PreChatContact {
  name: string;
  email: string;
  countryCode: string;
  phone: string;
}

export default function PreChatForm({
  botName,
  color,
  apiKey,
  sessionId,
  credentials = "same-origin",
  onComplete,
}: {
  botName: string;
  color: string;
  apiKey: string;
  sessionId: string;
  credentials?: RequestCredentials;
  onComplete: (contact: PreChatContact) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("+1");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setError(null);
    const n = name.trim();
    const e = email.trim();
    const digits = phone.replace(/\D/g, "");
    if (!n) { setError("Please enter your name."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setError("Please enter a valid email address."); return; }
    if (digits.length < 6 || digits.length > 15) { setError("Please enter a valid phone number."); return; }
    if (!consent) { setError("Please agree to the data-collection notice to continue."); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/v1/chatbots/${apiKey}/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials,
        body: JSON.stringify({
          sessionId,
          name: n,
          email: e,
          countryCode: country,
          phone: digits,
          consent: true,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => null);
        setError(d?.message || "Couldn't submit your details. Please try again.");
        return;
      }
      onComplete({ name: n, email: e, countryCode: country, phone: digits });
    } catch {
      setError("Couldn't submit your details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="w-full max-w-md mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm p-6 text-slate-900"
      data-testid="prechat-gate"
    >
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: color }}>
          <Bot className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-lg font-semibold leading-tight">{botName}</h1>
          <p className="text-xs text-slate-500">Before we start, please introduce yourself.</p>
        </div>
      </div>

      <form className="mt-5 space-y-4" onSubmit={(ev) => { ev.preventDefault(); submit(); }}>
        <div className="space-y-1.5">
          <label htmlFor="pc-name" className="text-sm font-medium">Name</label>
          <input
            id="pc-name"
            type="text"
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            placeholder="Your full name"
            autoComplete="name"
            className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-0"
            style={{ ["--tw-ring-color" as any]: color }}
            data-testid="input-prechat-name"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="pc-email" className="text-sm font-medium">Email</label>
          <input
            id="pc-email"
            type="email"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2"
            style={{ ["--tw-ring-color" as any]: color }}
            data-testid="input-prechat-email"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="pc-phone" className="text-sm font-medium">Phone</label>
          <div className="flex gap-2">
            <select
              value={country}
              onChange={(ev) => setCountry(ev.target.value)}
              className="bg-white border border-slate-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 max-w-[8.5rem]"
              style={{ ["--tw-ring-color" as any]: color }}
              aria-label="Country dialling code"
              data-testid="select-prechat-country"
            >
              {PRECHAT_COUNTRY_CODES.map((c) => (
                <option key={c.label} value={c.code}>{c.label}</option>
              ))}
            </select>
            <input
              id="pc-phone"
              type="tel"
              value={phone}
              onChange={(ev) => setPhone(ev.target.value)}
              placeholder="Phone number"
              autoComplete="tel"
              className="flex-1 min-w-0 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2"
              style={{ ["--tw-ring-color" as any]: color }}
              data-testid="input-prechat-phone"
            />
          </div>
        </div>

        <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(ev) => setConsent(ev.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300"
            data-testid="checkbox-prechat-consent"
          />
          <span>
            I consent to {botName} collecting my name, email, and phone number to respond to
            my enquiry. This information is kept confidential and is not shared with third parties or
            used for any purpose unrelated to this conversation. Education records are handled in
            accordance with FERPA.
          </span>
        </label>

        {error && (
          <p className="text-xs text-red-600" data-testid="text-prechat-error">{error}</p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full text-white text-sm font-medium rounded-lg px-4 py-2.5 disabled:opacity-50 transition-opacity"
          style={{ backgroundColor: color }}
          data-testid="button-prechat-submit"
        >
          {submitting ? "Starting…" : "Start chat"}
        </button>
      </form>
    </div>
  );
}
